import { FolderKanban } from 'lucide-react';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import { formatDate } from '@/lib/format';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { useOpsOrgProjects, type OrgProjectRow } from '@/api/operations-org-hooks';

const columns: ColumnDef<OrgProjectRow>[] = [
  {
    id: 'name',
    header: 'Project name',
    accessorFn: (r) => r.name,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 500 }}>
        {row.original.name}
      </span>
    ),
  },
  {
    id: 'teams_count',
    header: 'Teams',
    accessorFn: (r) => r.teams_count,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.teams_count.toLocaleString('en-US')}
      </span>
    ),
  },
  {
    id: 'resources_count',
    header: 'Resources',
    accessorFn: (r) => r.resources_count,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.resources_count.toLocaleString('en-US')}
      </span>
    ),
  },
  {
    id: 'project_id',
    header: 'ID',
    accessorFn: (r) => r.id,
    cell: ({ row }) => (
      <span
        style={{
          fontFamily: 'monospace',
          fontSize: 'var(--vn-font-size-xs)',
          color: 'var(--vn-fg-muted)',
        }}
      >
        {row.original.id}
      </span>
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorFn: (r) => r.created_at,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
];

/** Org detail Projects tab — projects scoped to this organization. */
export function OpsOrgProjectsTab({ orgId }: { orgId: string }) {
  const { data, isLoading, isError, refetch } = useOpsOrgProjects(orgId);

  if (isError) return <TabError title="Failed to load projects" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={5} />;

  return (
    <DataTable
      columns={columns}
      data={data ?? []}
      getRowId={(r) => r.id}
      emptyState={
        <EmptyState
          icon={<FolderKanban size={24} />}
          title="No projects"
          description="This organization has no projects yet."
        />
      }
    />
  );
}
